import * as React from 'react';

import { useFeature } from '../../../Hooks/useFeature';

/** Masks a value for read-only/sensitive access. Shared so every surface masks identically. */
export function maskValue(v: unknown): string {
  const s = v == null ? '' : String(v);
  return s ? '••••••' : '—';
}

interface IGatedFieldProps {
  /** Full capability path, e.g. 'page.hrcDashboard/modal.caseDetail/tab.caseDetail.empData/field.emp.ssn'. */
  capability: string;
  /**
   * Render function receiving the resolved access. `readOnly` is true for level-1/sensitive
   * (mask or disable), false for level-2 (editable). Not called when hidden.
   */
  children: (ctx: { readOnly: boolean; accessLevel: number }) => React.ReactNode;
  /** Rendered when the capability is hidden (accessLevel 0). Defaults to nothing. */
  fallback?: React.ReactNode;
}

/**
 * Data-driven field gate. Hides at accessLevel 0, marks read-only at 1 (or when the node is
 * sensitive), editable at 2 — straight from the feature tree, no per-field hardcoding. Consumers
 * decide how to honor `readOnly` (mask a display value, or disable an input).
 */
export function GatedField({ capability, children, fallback = null }: IGatedFieldProps): React.ReactElement {
  const { enabled, readOnly, accessLevel, isSensitive } = useFeature(capability);
  if (!enabled) return <>{fallback}</>;
  return <>{children({ readOnly: readOnly || isSensitive, accessLevel })}</>;
}
